// The board's theme, ported from today's lib/theme.ts:
// System, Light or Dark, chosen in the header and remembered on this device.
//
// The choice is `board.theme` in localStorage: `light` or `dark`; no key is
// System, which follows the system's colour scheme. The effective theme is
// always on <html data-theme="light|dark">: the stylesheets read it.
// main.tsx calls watchTheme before connecting or rendering; the Mac bundle
// permits no separate startup script. This module keeps it current as the
// choice is made on this page, as another page of the board makes it (another tab, the other
// pane of the Mac app's split, another window), and as the system changes
// while the choice is System.
import { useSyncExternalStore } from 'react'

export type Theme = 'light' | 'dark'
/** What the header offers. System follows the system's colour scheme. */
export type ThemeChoice = 'system' | Theme

export const THEME_KEY = 'board.theme'
/** `--pp-bg` of styles/tokens.css: the header's colour, which the browser's own chrome takes. */
export const THEME_COLOR: Record<Theme, string> = { light: '#fbfbfc', dark: '#111118' }

const ATTRIBUTE = 'data-theme'
const DARK = '(prefers-color-scheme: dark)'

// A choice that storage would not keep (a private window): it holds on this
// page until it is reloaded.
let unstored: ThemeChoice | null = null

/** The choice: what storage holds, and System when it holds none of the two or cannot be read. */
export function themeChoice(): ThemeChoice {
  if (unstored !== null) return unstored
  try {
    const stored = window.localStorage.getItem(THEME_KEY)
    return stored === 'light' || stored === 'dark' ? stored : 'system'
  } catch {
    return 'system'
  }
}

function systemScheme(): MediaQueryList | null {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function' ? window.matchMedia(DARK) : null
}

/** The system's colour scheme now; light where it cannot be asked. */
export function systemTheme(): Theme {
  return systemScheme()?.matches ? 'dark' : 'light'
}

/** The theme a choice comes to under a system colour scheme. */
export function resolveTheme(choice: ThemeChoice, system: Theme): Theme {
  return choice === 'system' ? system : choice
}

function tell(name: string, content: string): void {
  const meta = document.querySelector(`meta[name="${name}"]`)
  if (meta && meta.getAttribute('content') !== content) meta.setAttribute('content', content)
}

/**
 * Put the effective theme on the page: `data-theme`
 * on <html>, and what the browser is told of it, the colour scheme of what it
 * draws itself and the header's colour. Writes nothing that already stands.
 */
export function applyTheme(): Theme {
  const theme = resolveTheme(themeChoice(), systemTheme())
  const root = document.documentElement
  if (root.getAttribute(ATTRIBUTE) !== theme) root.setAttribute(ATTRIBUTE, theme)
  tell('color-scheme', theme)
  tell('theme-color', THEME_COLOR[theme])
  return theme
}

const listeners = new Set<() => void>()

/** The page takes what the choice and the system say now, and whoever shows the choice hears of it. */
function refresh(): void {
  applyTheme()
  for (const listener of listeners) listener()
}

/**
 * Choose System, Light or Dark. It applies at once, and is remembered on this
 * device: every other page of the board open here follows (see `watchTheme`).
 */
export function setThemeChoice(choice: ThemeChoice): void {
  try {
    if (choice === 'system') window.localStorage.removeItem(THEME_KEY)
    else window.localStorage.setItem(THEME_KEY, choice)
    unstored = null
  } catch {
    unstored = choice
  }
  refresh()
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/** The choice, kept current as it is made, on this page or on another. */
export function useThemeChoice(): ThemeChoice {
  return useSyncExternalStore(subscribe, themeChoice, () => 'system')
}

/**
 * Keep the page's theme current from here on, and answer what stops it. The
 * app calls it once as it starts (main.tsx), before connecting or rendering.
 */
export function watchTheme(): () => void {
  const system = systemScheme()
  // The system changed: it is the theme only while the choice is System.
  const onSystem = () => {
    if (themeChoice() === 'system') refresh()
  }
  // Another page of the board made a choice: a browser tells every page of
  // an origin but the one that wrote. `key` is null when storage was cleared.
  const onStorage = (event: StorageEvent) => {
    if (event.key !== null && event.key !== THEME_KEY) return
    unstored = null
    refresh()
  }
  // A page that heard of neither (asleep, or kept in the page cache while
  // another was shown) reads both again as it is looked at.
  const onShown = () => refresh()

  system?.addEventListener('change', onSystem)
  window.addEventListener('storage', onStorage)
  document.addEventListener('visibilitychange', onShown)
  window.addEventListener('focus', onShown)
  window.addEventListener('pageshow', onShown)
  refresh()
  return () => {
    system?.removeEventListener('change', onSystem)
    window.removeEventListener('storage', onStorage)
    document.removeEventListener('visibilitychange', onShown)
    window.removeEventListener('focus', onShown)
    window.removeEventListener('pageshow', onShown)
  }
}

/** Forget a choice storage would not keep. Only tests call this: a page forgets it by reloading. */
export function resetThemeForTests(): void {
  unstored = null
}
